import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { Box, Typography, useTheme } from '@mui/material';
import Order from '../Order';
import OrderStatusFooter from './component/OrderStatusFooter';
import OrderStatusAddress from './component/OrderStatusAddress';
import OrderStatusStatus from './component/OrderStatusStatus';
import OrderSteps from '../OrderSteps';
import { Tag, Button, getIcon } from '@dishopsaas/design-system';
import { getDayFormat, getStatus, getPaymentTypeData, formatPhoneWithPlus } from '../../utils';
import OrderStatusEstimationTime from './component/OrderStatusEstimationTime';
import getStyles from './OrderStatus.styles';
import { getActiveStep } from './OrderStatus.services';
import { BLACK, ORDER_FLOW_TYPE, TERTIARY, WHITE } from '../../constants';
import { useAppSelector } from '../../redux/hook';
import { useNavigate } from "react-router-dom";
import {
  ORDER_PHASE_CONTACTING,
  ORDER_PHASE_PREPARING,
  ORDER_PHASE,
  PICKUP_STATUS,
  PROMOTION_DELIVERY_FREE,
  ORDER_TYPE_DELIVERY,
  ORDER_PHASE_DELIVERING
} from '../../constants';
import OrderStatusEmergencyDelivery from './component/OrderStatusEmergencyDelivery';
import OrderTotal from '../Order/components/OrderTotal';
import { getPromotion } from "../../utils";
import { navigateWithQr } from '../../utils/routing.utils'
import { useLocation } from "react-router-dom";
import OrderDetailsContent from '../Order/components/OrderDetailsContent'
import LoyaltyBanner from "../../components/LoyaltyBanner/LoyaltyBanner";
import CloseButton from "../CloseButton";
type OrderStatusProps = {
  chargeId: string;
  isMobile: boolean;
};

const calculateEmergencyClickAndCollect = (order: any, delivery: any, orderStatus: string) => {
  const {
    orderFlowType,
    pickupStatus,
    orderType,
    selfPickupEligible,
    totalProductPriceOrder = 0,
    userServicePrice = 0,
    managementFee = 0,
    promotion,
  } = order;

  const orderNotCurrentlySupported =
    pickupStatus === PICKUP_STATUS.ORDER_NOT_CURRENTLY_SUPPORTED;
  const deliveryFreeReduction =
    promotion?.type === PROMOTION_DELIVERY_FREE ? 0 : userServicePrice;

  const clickAndCollectReduction =
    delivery?.emergency?.clickAndCollectConversionReduction ?? 0;

  const reductionBase = deliveryFreeReduction + managementFee;

  const reductionToClickAndCollect = clickAndCollectReduction
    ? (
      Math.round(clickAndCollectReduction * totalProductPriceOrder) / 100 +
      reductionBase
    ).toFixed(2)
    : reductionBase.toFixed(2);

  const isContactingEmergency =
    orderStatus === ORDER_PHASE.CONTACTING &&
    orderFlowType === ORDER_FLOW_TYPE.REVERSE &&
    orderNotCurrentlySupported;

  const isPreparingEmergency =
    orderStatus === ORDER_PHASE_PREPARING &&
    pickupStatus < PICKUP_STATUS.DRIVER_ON_THE_WAY_TO_RESTAURANT;

  const isDeliveryEmergency =
    (isContactingEmergency || isPreparingEmergency) &&
    orderType === ORDER_TYPE_DELIVERY &&
    selfPickupEligible &&
    delivery?.emergency?.enabled;

  return {
    reductionToClickAndCollect: reductionToClickAndCollect.replace(".", ","),
    isDeliveryEmergency,
  };
};
export default function OrderStatus({ chargeId, isMobile }: OrderStatusProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { orders } = useAppSelector((state) => state.orderReducer);
  const { delivery } = useAppSelector((state) => state.configurationReducer);
  const pendingOrder = useAppSelector((state) => state.pendingOrderReducer);
  const {
    totalProductPriceOrder: totalProductPrice,
    marketingCampaignPromoAmount,
    totalPriceOrder,
    managementFee,
  } = pendingOrder;
  const order = orders[chargeId];
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = getStyles();
  const location = useLocation();
  useEffect(() => {
    if (_.isEmpty(order)) {
      navigateWithQr(navigate, location.pathname, "/");
    }
  }, [order, navigate]);

  if (_.isEmpty(order)) return null;

  const { status, orderFlowType, pickupStatus, orderType, date, orderNumber } = order;

  const orderFlowReverse = orderFlowType === ORDER_FLOW_TYPE.REVERSE;

  const activeStep = getActiveStep(
    status,
    orderFlowReverse,
    orderType,
    pickupStatus
  );

  const displayEmergencyBlock =
    status === ORDER_PHASE_CONTACTING ||
    status === ORDER_PHASE_PREPARING;

  const { commande, paymentType } = order;
  const isPendingOrder = !commande;
  const { paymentTypeLabel, icon } = getPaymentTypeData(paymentType);
  const { reductionToClickAndCollect, isDeliveryEmergency } =
    calculateEmergencyClickAndCollect(order, delivery, status);
  const desktopStyles = {
    m: 4,
    backgroundColor: theme.palette.primary.light,
    borderRadius: 2,
  };


  const EmergencyBlock = isDeliveryEmergency && (
    <Box sx={isMobile ? {} : desktopStyles}>
      <OrderStatusEmergencyDelivery
        isDeliveryEmergency={isDeliveryEmergency}
        reductionToClickAndCollect={reductionToClickAndCollect}
        order={order}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    </Box>
  );
  return (
    <div style={styles.layout(isMobile) as React.CSSProperties}>
      {!isMobile && (
        <div style={styles.leftPanel(isMobile)}>
          <OrderStatusStatus
            order={order}
            chargeId={order?.chargeId}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isMobile={isMobile}
          />
        </div>
      )}
      <Box style={styles.rightPanel(isMobile)}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>

          <Box sx={styles.sectionContainer}>
            <Box sx={styles.column}>
              <Box sx={styles.row}>
                <Typography variant="h5">
                  Commande n°{orderNumber} {" "} {getDayFormat(date, "du")}
                </Typography>

                <Tag
                  label="Commande en cours"
                  shape="linear"
                  borderRadius={16}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>

                <Typography variant="caption">
                  {getStatus(order)}
                </Typography>

                {order.status === ORDER_PHASE_DELIVERING && order?.numeroDep && (
                  <Box
                    onClick={() => window.open(`tel:${order.numeroDep}`)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                      color: "primary.main",
                    }}
                  >
                    {getIcon("Phone", { size: 13 })}

                    <Typography
                      variant="caption"
                      sx={{
                        textDecoration: "underline",
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      {formatPhoneWithPlus(order.numeroDep)}
                    </Typography>
                  </Box>
                )}
              </Box>

            </Box>
          </Box>

          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflowY: !isMobile && "auto", minHeight: 0 }}>
            <OrderSteps order={order} activeStep={activeStep} />

            {isMobile && (
              <div style={styles.leftPanel(isMobile)}>
                <OrderStatusStatus
                  order={order}
                  chargeId={order?.chargeId}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  isMobile={isMobile}
                />
              </div>
            )}

            {isMobile && !showDetails && (
              <Box sx={styles.mobileBottomBar}>
                <OrderStatusEstimationTime order={order} isMobile={isMobile} />
                <Button
                  label="Voir ma commande"
                  size="large"
                  onClick={() => setShowDetails(true)}
                  width="100%"
                  colorType={TERTIARY}
                  sx={{
                    bgcolor: BLACK,
                    color: WHITE,

                    '&:hover': {
                      bgcolor: BLACK,
                      color: WHITE,


                    },
                  }}
                />
              </Box>
            )}
            {isMobile && showDetails && (
              <Box sx={styles.mobileBottomSheet}>

                <Box sx={styles.mobileHandle}>


                  <Box
                    onClick={() => setShowDetails(false)}
                    sx={styles.mobileHandleBar}
                  >
                    <CloseButton onClick={() => setShowDetails(false)}></CloseButton>
                  </Box>
                </Box>
                <Box sx={{ flex: 1, overflowY: "auto" }}>

                  <OrderDetailsContent
                    order={order}
                    showFees={true}
                    deleteOption={false}
                    orderType={orderType}
                    totalPrice={order.totalPriceOrder}
                    isMobile={isMobile}
                    isPastOrder={false}
                    EmergencyBlock={
                      displayEmergencyBlock && EmergencyBlock
                    }
                  />

                  <Box sx={styles.buttonContainer}>
                    <OrderStatusEstimationTime
                      order={order}
                      isMobile={isMobile}
                    />

                    <OrderStatusFooter
                      order={order}
                      setIsLoading={setIsLoading}
                    />
                  </Box>

                </Box>
              </Box>
            )}
            {!isMobile && (
              <>
                {displayEmergencyBlock && EmergencyBlock}
                <OrderStatusAddress order={order} />
                <Order order={order} showCart showFees />
              </>
            )}
          </Box>
        </Box>

        {!isMobile && (
          <Box sx={{ ...styles.footerContainer, flexShrink: 0 }}>
            <Box sx={{ padding: "20px 16px" }}>
              <OrderTotal
                commande={commande}
                promotion={
                  isPendingOrder
                    ? getPromotion(totalProductPrice)
                    : order.promotion
                }
                showFees
                isOrderFreeDelivery={order.freeDelivery}
                marketingCampaignPromoAmount={
                  isPendingOrder
                    ? marketingCampaignPromoAmount
                    : order.marketingCampaignPromoAmount
                }
                totalPriceOrder={
                  isPendingOrder ? totalPriceOrder : order.totalPriceOrder
                }
                totalProductPriceOrder={order.totalProductPriceOrder}
                managementFee={
                  isPendingOrder ? managementFee : order.managementFee
                }
                isPast={false}
              />
            </Box>
            <Box sx={styles.paymentBox}>
              <Typography variant="h5" sx={styles.capitalize}>
                Paiement
              </Typography>
              <LoyaltyBanner commande={commande} />
              <Box sx={styles.paymentRow}>
                <Box sx={styles.paymentLabel}>
                  {getIcon(icon, { size: 20 })}
                  <Typography variant="body1">{paymentTypeLabel}</Typography>
                </Box>
              </Box>
            </Box>
            <Box sx={styles.buttonContainer}>
              <OrderStatusEstimationTime order={order} isMobile={isMobile} />
              <OrderStatusFooter order={order} setIsLoading={setIsLoading} />
            </Box>

          </Box>
        )}
      </Box>
    </div >
  );
}